import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFetchingIssuerFromState,
  selectTopLevelErrorFromState,
} from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import { PaymentNetInputPageFlatFields } from 'src/app/RegisterAccount/RegisterAccount'
import RegisterAccountPaymentNetinputForm from 'src/components/RegisterAccountPaymentNetInputForm/RegisterAccountPaymentNetInputForm'
import { Environments } from 'src/configurations'
import { useTranslate } from 'src/localization/Trns'
import { connectedPush } from 'src/navigation/Lnk'
import { registerAccountUrl } from 'src/navigation/urls'
import {
  getIssuer,
  getIssuers,
} from 'src/redux/actions/data/issuerIntegrators.actions'
import { selectEnvironmentFromState } from 'src/redux/selectors/session.selectors'
import colors from 'src/style/variables.json'
import { css } from 'styled-components'

interface OnboardingStepThreeJPMProps {
  currentStep: number
  totalSteps: number
  setStep: (val: number) => void
  flatFields: PaymentNetInputPageFlatFields
  onFlatFieldChange: (
    fieldName: keyof PaymentNetInputPageFlatFields,
    value: PaymentNetInputPageFlatFields[keyof PaymentNetInputPageFlatFields],
  ) => void
  flatFieldsErrors: Partial<
    Record<
      keyof PaymentNetInputPageFlatFields,
      Array<React.ReactElement | string>
    >
  >
  setErrorsAndReturnDidPassValidation: () => boolean
  title: React.ReactNode
  subtitle: React.ReactNode
  cardInfoHeader: React.ReactElement
}

const OnboardingStepThreeJPM: React.FC<OnboardingStepThreeJPMProps> = (
  props,
) => {
  const translate = useTranslate()

  const fetchingIssuer = useSelector(selectFetchingIssuerFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const env = useSelector(selectEnvironmentFromState)
  const { analytics } = useAnalyticsContext()

  const stepThreeSideInfo = (
    <div
      css={css`
        border-radius: 0px 10px 10px 0px;
        max-width: 350px;
        background: #3a2206;
      `}
    >
      <div
        css={css`
          margin: 120px 40px;
        `}
      >
        <div
          css={css`
            ${getFontCss(
              20,
              styleConstants.colors.white,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 16px;
          `}
        >
          {translate('Do more with your business account')}
        </div>
        <div
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.white,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 16px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.white,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate(
              'Empower non-carded employees who need to make purchases',
            )}
          </div>
          {translate(
            'Control employee spending, gain real-time visibility over every transaction, and keep everyone accountable for their own charges and receipts—perfect for travel expenses.',
          )}
        </div>
        <div
          css={css`
            ${getFontCss(
              14,
              styleConstants.colors.white,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 16px;
          `}
        >
          <div
            css={css`
              ${getFontCss(
                14,
                styleConstants.colors.white,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            {translate('Organize company spending with budget allocation')}
          </div>
          {translate(
            'Track spending for teams, projects, clients, multi-locations, and vendors. Share a budget with anyone responsible for spending so they can create virtual cards under your purview.',
          )}
        </div>
      </div>
    </div>
  )

  const dispatch = useDispatch()

  useEffect((): void => {
    dispatch(getIssuers())
    const issuerId =
      env === Environments.Prod
        ? 'iss_6438b9bd2ab3c40a10fac3'
        : 'ii_6026ef7985cf7a583d2f5f'
    dispatch(getIssuer(issuerId))
  }, [])

  const onAccountBinInputPageSubmit = (): void => {
    analytics?.track('register card - continue', {
      intent: 'continue',
      interaction: 'button press',
    })

    const accountBinInputPageFlatFieldsPassedValidation =
      props.setErrorsAndReturnDidPassValidation()
    if (accountBinInputPageFlatFieldsPassedValidation) {
      dispatch(
        connectedPush({
          pathname: registerAccountUrl(),
          search: '',
          state: {
            currentPage: 2,
            paymentNetInfoFlatFields: props.flatFields,
          },
          hash: '',
        }),
      )
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-grow: 1;
      `}
    >
      <div
        css={css`
          flex-grow: 1;
        `}
      >
        <RegisterAccountPaymentNetinputForm
          css={css`
            padding: 80px;
          `}
          flatFields={props.flatFields}
          onFlatFieldChange={props.onFlatFieldChange}
          flatFieldsErrors={props.flatFieldsErrors}
          loading={fetchingIssuer}
          onSubmit={onAccountBinInputPageSubmit}
          cardInfoHeader={props.cardInfoHeader}
          stepElement={
            <div
              css={css`
                ${getFontCss(
                  12,
                  colors.tokensColorJPM.JPMbrown500,
                  styleConstants.fontFamilies.regular,
                )};
                margin-bottom: 20px;
              `}
            >
              {translate('Step ${currentStep} of ${totalSteps}', {
                currentStep: props.currentStep.toString(),
                totalSteps: props.totalSteps.toString(),
              })}
            </div>
          }
          title={props.title}
          subtitle={props.subtitle}
          topLevelError={topLevelError}
        />
      </div>
      {stepThreeSideInfo}
    </div>
  )
}

export default OnboardingStepThreeJPM
